import apiClient from "../../../lib/api_client";

// The cart lives on the server so it follows the salesperson from one device to
// the next. localStorage is still written on every change, but only as an
// offline copy: the server is what a fresh login reads from.

// A cart belongs to a client, an event and a purchase order together - the same
// client shops for more than one event, and one event can be quoted under more
// than one PO, so each combination is its own cart. A client that already
// exists is keyed by its code; one being typed in for the first time has no
// code yet, so the name it was entered under is used instead, which is stable
// enough to follow the same cart across devices until the client is created.
export const getCartKey = (clientData) => {
  if (!clientData) return null;

  const code = clientData?.clientCode?.trim?.();
  const clientName = (
    clientData?.newClientName ||
    clientData?.name ||
    ""
  ).trim();

  const clientCode = code || (clientName ? `NEW:${clientName}` : "");
  if (!clientCode) return null;

  return {
    clientCode,
    clientName,
    eventTypeId: (clientData?.eventTypeId || "").trim(),
    eventName: (clientData?.eventName || clientData?.event || "").trim(),
    purchaseNo: (clientData?.purchaseNo || "").trim(),
  };
};

// Same key flattened to a string, so a change of client or event is a plain
// comparison rather than an object diff.
export const cartKeyId = (key) =>
  key ? `${key.clientCode}::${key.eventTypeId}::${key.purchaseNo || ""}` : null;

// Reads the client the portal is currently working with. Every screen already
// keeps this in localStorage, so the cart reads it from the same place rather
// than threading it through props.
export const readClientData = () => {
  try {
    return JSON.parse(localStorage.getItem("ClientData")) || null;
  } catch (error) {
    return null;
  }
};

const keyParams = (key) => ({
  clientCode: key?.clientCode || "",
  eventTypeId: key?.eventTypeId || "",
  purchaseNo: key?.purchaseNo || "",
});

// What this client was last carted under. With a purchaseNo it answers which
// event that PO was raised under; without one it answers what the client was
// last working on, which is what fills the PO number and the event in as soon
// as a client is picked.
export const lookupSavedCart = async ({ clientCode, purchaseNo = "" }) => {
  const res = await apiClient.get("/cart/lookup", {
    params: { clientCode, purchaseNo },
  });
  return res?.data?.data || null;
};

export const fetchServerCart = async (key) => {
  const res = await apiClient.get("/cart", { params: keyParams(key) });
  return res?.data?.data || null;
};

export const pushServerCart = async (payload) => {
  const res = await apiClient.post("/cart/sync", payload);
  return res?.data?.data || null;
};

// Removes the cart for this client and event. The history document is left in
// place, with the clear recorded in it. action says why: "CLEAR" when the user
// emptied it, "ORDERED" when it became an order.
export const deleteServerCart = async (key, action = "CLEAR") => {
  const res = await apiClient.delete("/cart", {
    params: { ...keyParams(key), action },
  });
  return res?.data || null;
};

export const fetchServerCartLogs = async (key, limit = 50) => {
  const res = await apiClient.get("/cart/logs", {
    params: { ...keyParams(key), limit },
  });
  return res?.data?.data || null;
};
